"use client";

import { useState, useEffect, useCallback } from "react";
import { useTranslations } from "next-intl";
import Modal from "./Modal";
import { KeyMark } from "./Logo";

let setOpenExternal: ((v: boolean) => void) | null = null;

export function openLoginModal() {
  setOpenExternal?.(true);
}

export default function LoginModal() {
  const t = useTranslations("loginModal");
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<"login" | "forgot">("login");

  useEffect(() => {
    setOpenExternal = setOpen;
    return () => {
      setOpenExternal = null;
    };
  }, []);

  // Wire any element with [data-modal="login"] anywhere on the page
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      const trigger = t.closest('[data-modal="login"]');
      if (trigger) {
        e.preventDefault();
        setStep("login");
        setOpen(true);
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  const close = useCallback(() => setOpen(false), []);

  return (
    <Modal open={open} onClose={close}>
      {step === "login" ? (
        <>
          <div className="flex flex-col items-center text-center mb-6">
            <KeyMark size={48} uid="login" />
            <h2 className="h-display text-2xl font-semibold mt-3.5 mb-1">
              {t("welcomeTitle")}
            </h2>
            <p className="text-ss-muted text-sm">
              {t("welcomeSub")}
            </p>
          </div>
          <div className="flex flex-col gap-2.5 mb-4">
            <input
              className="w-full px-3.5 py-3 border border-ss-line rounded-[10px] outline-none focus:border-ss-blue"
              placeholder={t("emailPlaceholder")}
              type="email"
              autoComplete="email"
            />
            <input
              className="w-full px-3.5 py-3 border border-ss-line rounded-[10px] outline-none focus:border-ss-blue"
              placeholder={t("passwordPlaceholder")}
              type="password"
              autoComplete="current-password"
            />
          </div>
          <button
            type="button"
            className="w-full bg-ss-blue text-white font-semibold py-3.5 rounded-full text-base hover:bg-ss-blue-dark"
            onClick={() => {
              window.localStorage.setItem("ss-signed-in", "1");
              close();
              window.location.href = "/account";
            }}
          >
            {t("signIn")}
          </button>
          <div className="text-center mt-3.5">
            <button
              type="button"
              className="text-ss-blue text-sm font-semibold hover:underline"
              onClick={() => setStep("forgot")}
            >
              {t("forgotPassword")}
            </button>
          </div>
          <div className="flex items-center gap-3 my-5 text-ss-muted text-xs">
            <span className="flex-1 h-px bg-ss-line" />
            {t("or")}
            <span className="flex-1 h-px bg-ss-line" />
          </div>
          <div className="flex flex-col gap-2">
            <SocialBtn label={t("continueGoogle")} />
            <SocialBtn label={t("continueApple")} />
            <button
              type="button"
              className="w-full bg-[#25d366] text-white font-semibold py-3 rounded-full hover:bg-[#1ebe5a]"
              onClick={() => {
                window.localStorage.setItem("ss-signed-in", "1");
                close();
                window.location.href = "/account";
              }}
            >
              {t("continueWhatsapp")}
            </button>
          </div>
          <p className="text-center text-xs text-ss-muted mt-4">
            {t("noAccount")}{" "}
            <a href="#" className="text-ss-blue font-semibold">
              {t("signUp")}
            </a>
          </p>
        </>
      ) : (
        <>
          <div className="text-center mb-5">
            <div className="w-14 h-14 rounded-2xl bg-ss-blue-50 text-ss-blue inline-grid place-items-center mb-3.5 text-xl">
              🔒
            </div>
            <h2 className="h-display text-2xl font-semibold mb-1">
              {t("forgotTitle")}
            </h2>
            <p className="text-ss-muted text-sm">
              {t("forgotSub")}
            </p>
          </div>
          <input
            className="w-full px-3.5 py-3 border border-ss-line rounded-[10px] outline-none focus:border-ss-blue mb-3"
            placeholder={t("emailExamplePlaceholder")}
            type="email"
          />
          <button
            type="button"
            className="w-full bg-ss-blue text-white font-semibold py-3.5 rounded-full text-base hover:bg-ss-blue-dark"
            onClick={() => {
              alert(t("resetLinkSent"));
              close();
            }}
          >
            {t("sendResetLink")}
          </button>
          <div className="text-center mt-4">
            <button
              type="button"
              className="text-ss-blue text-sm font-semibold hover:underline"
              onClick={() => setStep("login")}
            >
              {t("backToSignIn")}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

function SocialBtn({ label }: { label: string }) {
  return (
    <button
      type="button"
      className="w-full bg-white border border-ss-line text-ss-text font-semibold py-3 rounded-full hover:border-ss-text"
    >
      {label}
    </button>
  );
}
